import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useLocale } from '../../utils/LocaleContext';
import { getAllLogsAction } from '../../redux/actions/userActions';
import { statusLabel as recordStatusLabel } from '../../utils/i18nLabels';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── CSS keyframe injection ───────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('dash-anim-css')) {
  const s = document.createElement('style');
  s.id = 'dash-anim-css';
  s.textContent = `
    @keyframes dashPulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
    @keyframes dashSlideIn { from{opacity:0;transform:translateY(-12px)} to{opacity:1;transform:translateY(0)} }
    @keyframes dashFlash { 0%,100%{background:transparent} 30%{background:rgba(128,0,32,0.08)} }
    @keyframes dashBounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
    .dash-new-item { animation: dashSlideIn 0.35s ease, dashFlash 3s ease forwards; }
    .dash-flash { animation: dashFlash 1.5s ease; }
    .dash-pulse { animation: dashPulse 1.5s infinite; }
    /* A bounded list inside a dashboard card.
       A card whose content is scrollable must LOOK scrollable. An invisible
       overflow is worse than no overflow, because nothing tells you the rest
       is there — which is the complaint this rule answers.
       scrollbar-gutter:stable is doing the load-bearing work. Without it
       Chromium gives an OVERLAY scrollbar: it reserves no width and paints
       only while you hover or scroll, so the bar is absent exactly when a
       user needs to be told it exists. Measured: gutter 0px with
       scrollbar-width/-color set, 8px with this. It also reserves the gutter
       when the list is short, so the rows do not shift when the list grows
       past the fold.
       The standard scrollbar-* properties are deliberately quarantined to
       browsers with no ::-webkit-scrollbar support (Firefox). Set globally
       they WIN in Chromium and put the overlay bar back. */
    .dash-scroll { overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; }
    .dash-scroll::-webkit-scrollbar { width: 8px; }
    .dash-scroll::-webkit-scrollbar-track { background: transparent; }
    .dash-scroll::-webkit-scrollbar-thumb { background: rgba(128,0,32,0.30); border-radius: 4px; }
    .dash-scroll:hover::-webkit-scrollbar-thumb { background: rgba(128,0,32,0.45); }
    @supports not selector(::-webkit-scrollbar) {
      .dash-scroll { scrollbar-width: thin; scrollbar-color: rgba(128,0,32,0.30) transparent; }
    }
  `;
  document.head.appendChild(s);
}

// No hardcoded mock — the dashboard must render real tenant-scoped data.
// Empty state (zeros / empty arrays) when the tenant has no claims yet.

const useDashboardFunctions = globalSocket => {
  useLocale();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);
  const [ackingId, setAckingId] = useState(null);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);

  // ── Live state ───────────────────────────────────────────
  const userLogin = useSelector(state => state.userLogin);
  const loggedinUser = useSelector(state => state.loggedinUser);
  const currentUser = userLogin?.userInfo?.data?.user;
  const userRole = loggedinUser?.userInfo?.data?.data?.role || currentUser?.role;
  const logsList = useSelector(state => state.logsList);

  // Note: previously the Dashboard force-redirected `admin` (SaaS God) to
  // /platform on mount. That behaviour was removed 2026-07-04 so SaaS God
  // can actually view the tenant Dashboard when they navigate there
  // (per A6 test failure). SaaS God still lands on /platform after login —
  // see LoginScreen/useLoginFunctions.js — but no longer bounces from other
  // pages.
  const orgId = currentUser?.org || currentUser?._id;

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [liveActivity, setLiveActivity] = useState([]);
  const [reggieCount, setReggieCount] = useState(0);
  const [signalFlash, setSignalFlash] = useState(false);
  const [metricFlash, setMetricFlash] = useState({});
  const socketRef = useRef(null);

  const addActivity = useCallback(item => {
    setLiveActivity(prev => [{ ...item, _id: Date.now() + Math.random(), isLive: true }, ...prev].slice(0, 20));
  }, []);

  const flashMetric = useCallback(key => {
    setMetricFlash(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setMetricFlash(prev => ({ ...prev, [key]: false })), 1600);
  }, []);

  // Claims-related fetches are conditional on the tenant having claims
  // enabled. Otherwise we skip the API call entirely — no 403, no dataError
  // banner, no phantom fallback. The screen renders zero-state cleanly.
  // Has the tenant config actually arrived? The reducer's initial state is
  // `{ config: null }`, so `features.claims` reads false BEFORE the config
  // is known and false when the feature is genuinely off — two different
  // facts that the code below used to conflate. Unresolved took the
  // "feature disabled" branch, which set dashData to {} and loading to
  // false, and the screen confidently rendered 0 active cases / 0 total /
  // no activity for a tenant with 25 of them.
  //
  // "Resolved" means the question has been ANSWERED, not that the answer was
  // an object. Three ways it can be answered, mirroring the test
  // RegureSidebar already uses (see isTenantConfigResolved there — keep the
  // two in step rather than maintaining a second interpretation):
  //   - a config arrived;
  //   - the request failed, so the reducer holds an error;
  //   - the caller is SaaS God, who has no org and for whom no tenant config
  //     will ever load.
  // Guarding only on `config != null` waited for an answer that never comes
  // in the last two cases: TENANT_CONFIG_FAIL leaves `config` null, and
  // `/tenant/config` returns `data?.data || null`, so an org-less admin gets
  // null on SUCCESS too. fetchDashboard then returned before its try block,
  // so the `finally { setLoading(false) }` never ran — a permanent spinner,
  // with no dataError banner because the fetch never happened.
  const tenantConfigResolved = useSelector(state =>
    state?.tenantConfig?.config != null ||
    state?.tenantConfig?.error != null,
  ) || userRole === 'admin';
  const claimsEnabled = useSelector(state => Boolean(state?.tenantConfig?.config?.features?.claims));
  const riskSignalsEnabled = useSelector(state => Boolean(state?.tenantConfig?.config?.features?.riskSignals));
  const auditTrailEnabled = useSelector(state => Boolean(state?.tenantConfig?.config?.features?.auditTrail));

  const fetchDashboard = useCallback(async () => {
    // Nothing is knowable until the tenant config lands. Stay in the
    // loading state rather than fetching against a feature set we have not
    // read yet — the effect re-runs when tenantConfigResolved flips.
    if (!tenantConfigResolved) return;
    try {
      if (auditTrailEnabled) dispatch(getAllLogsAction(1));
      // The dashboard request is NOT swallowed. It used to be
      // `.catch(() => null)`, so a failed first load left dashData as {} —
      // the screen rendered 0 active cases, an empty donut and a blank
      // volume chart for a tenant with 25 cases, with no error shown, and
      // only a manual Refresh recovered it. A failure must be visible.
      let dashFailed = false;
      const [dashResp, riskResp, deadlinesResp] = await Promise.all([
        claimsEnabled
          ? axios.get(`${BASE_URL}/claims/dashboard`, { withCredentials: true })
              .catch(() => { dashFailed = true; return null; })
          : Promise.resolve(null),
        riskSignalsEnabled
          ? axios.get(`${BASE_URL}/risk-signals/summary`, { withCredentials: true }).catch(() => null)
          : Promise.resolve(null),
        // Real policy renewals within the next 90 days — replaces the
        // hardcoded DEADLINES mock that showed IJM policies + past dates.
        axios.get(`${BASE_URL}/policies/upcoming-deadlines?days=90`, { withCredentials: true }).catch(() => null),
      ]);
      if (deadlinesResp?.data?.data) setUpcomingDeadlines(deadlinesResp.data.data);
      if (dashResp?.data?.data) {
        setDashData(dashResp.data.data);
        setDataError(false);
        setLastUpdated(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }));
      } else if (claimsEnabled && dashFailed) {
        // Surface it rather than rendering confident zeros.
        setDataError(true);
      } else if (!claimsEnabled) {
        // Feature disabled — treat as fresh empty response so the screen
        // renders a clean zero-state instead of stuck loading.
        setDashData({});
        setLastUpdated(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }));
      }
      if (riskResp?.data?.data) setRiskSummary(riskResp.data.data);
    } catch (err) {
      setDataError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantConfigResolved, claimsEnabled, riskSignalsEnabled, auditTrailEnabled, dispatch]);

  const handleAcknowledge = useCallback(async signalId => {
    setAckingId(signalId);
    try {
      await axios.patch(`${BASE_URL}/risk-signals/${signalId}/acknowledge`, {}, { withCredentials: true });
      setRiskSummary(prev =>
        prev
          ? {
              ...prev,
              recentSignals: prev.recentSignals?.filter(s => s._id !== signalId),
              totalActive: Math.max(0, (prev.totalActive || 1) - 1),
            }
          : prev,
      );
    } catch {
      /* silent */
    } finally {
      setAckingId(null);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ── Socket.IO live connection — reuse global socket from App.js ──
  useEffect(() => {
    if (!orgId || !globalSocket) return;
    const socket = globalSocket;
    socketRef.current = socket;

    // Join the org room for tenant-scoped dashboard events
    if (socket.connected) socket.emit('join:org', String(orgId));
    else socket.once('connect', () => socket.emit('join:org', String(orgId)));

    socket.on('org:online-users', ({ onlineUsers: ou }) => setOnlineUsers(ou || []));
    socket.on('online-users', ({ onlineUsers: ou }) => setOnlineUsers(ou || []));

    socket.on('dashboard:claim:created', data => {
      const liveData = {
        user: data.actor,
        client: null,
        type: data.type,
        action: t('dashboard.liveClaimCreated', { number: data.claimNumber }),
        userIp: '',
        actionTags: [],
        module: data.module,
        timestamp: Date.now(),
        logType: 'success',
        logDevice: 'dashboard',
        previousHash: '',
        entryHash: '',
        createdAt: new Date(Date.now()),
        updatedAt: new Date(Date.now()),
        __v: 0,
      };

      addActivity(liveData);
      setDashData(prev => (prev ? { ...prev, openClaims: (prev.openClaims || 0) + 1 } : prev));
      flashMetric('openClaims');
    });

    socket.on('dashboard:claim:statusChanged', data => {
      const liveData = {
        user: data.actor,
        client: null,
        type: 'claims',
        action: t('dashboard.liveClaimStatus', { number: data.claimNumber, from: recordStatusLabel(t, data.from), to: recordStatusLabel(t, data.to) }),
        userIp: '',
        actionTags: [],
        module: 'claims',
        timestamp: Date.now(),
        logType: 'success',
        logDevice: 'dashboard',
        previousHash: '',
        entryHash: '',
        createdAt: new Date(Date.now()),
        updatedAt: new Date(Date.now()),
        __v: 0,
      };

      addActivity(liveData);
      flashMetric('openClaims');
    });

    socket.on('dashboard:claim:assigned', data => {
      const liveData = {
        user: data.actor,
        client: null,
        type: data.type,
        action: t('dashboard.liveClaimAssigned', { number: data.claimNumber, name: data.assignedTo }),
        userIp: '',
        actionTags: [],
        module: data.module,
        timestamp: Date.now(),
        logType: 'success',
        logDevice: 'dashboard',
        previousHash: '',
        entryHash: '',
        createdAt: new Date(Date.now()),
        updatedAt: new Date(Date.now()),
        __v: 0,
      };

      addActivity(liveData);
      flashMetric('pendingReviews');
    });

    socket.on('dashboard:signal:created', data => {
      addActivity({ initials: 'AI', user: t('dashboard.riskEngine'), action: t('dashboard.liveSignalRaised', { severity: data.severity || '' }), detail: data.claimNumber || '', time: t('dashboard.justNow') });
      setRiskSummary(prev => {
        if (!prev) return prev;
        const bySev = { ...(prev.bySeverity || {}), [data.severity]: (prev.bySeverity?.[data.severity] || 0) + 1 };
        const recent = [data, ...(prev.recentSignals || [])].slice(0, 10);
        return { ...prev, totalActive: (prev.totalActive || 0) + 1, bySeverity: bySev, recentSignals: recent };
      });
      setSignalFlash(true);
      setTimeout(() => setSignalFlash(false), 1600);
    });

    socket.on('dashboard:signal:resolved', data => {
      if (data.resolvedCount) {
        setRiskSummary(prev => (prev ? { ...prev, totalActive: Math.max(0, (prev.totalActive || 0) - data.resolvedCount) } : prev));
      }
    });

    socket.on('dashboard:reserve:changed', data => {
      const liveData = {
        user: data.actor,
        client: null,
        type: data.type,
        action: t('dashboard.liveReserveUpdated', { number: data.claimNumber, from: data.oldAmount, to: data.newAmount }),
        userIp: '',
        actionTags: [],
        module: data.module,
        timestamp: Date.now(),
        logType: 'success',
        logDevice: 'dashboard',
        previousHash: '',
        entryHash: '',
        createdAt: new Date(Date.now()),
        updatedAt: new Date(Date.now()),
        __v: 0,
      };

      addActivity(liveData);
    });

    socket.on('dashboard:payment:recorded', data => {
      const liveData = {
        user: data.actor,
        client: null,
        type: data.type,
        action: t('dashboard.livePaymentRecorded', { amount: data.paidAmount, number: data.claimNumber }),
        userIp: '',
        actionTags: [],
        module: data.module,
        timestamp: Date.now(),
        logType: 'success',
        logDevice: 'dashboard',
        previousHash: '',
        entryHash: '',
        createdAt: new Date(Date.now()),
        updatedAt: new Date(Date.now()),
        __v: 0,
      };

      addActivity(liveData);
    });

    socket.on('dashboard:reggie:query', () => {
      setReggieCount(prev => prev + 1);
      flashMetric('reggieCount');
    });

    return () => {
      socketRef.current = null;
    };
  }, [orgId, globalSocket]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live metrics (API data takes priority; no static fallback).
  // null, not 0 — MetricCards renders null as '—'. A count of zero is a
  // measurement; the absence of a response is not, and the two must not
  // render the same way. A tenant that really has 0 open cases still gets
  // "0" here, because dashData.openClaims is then present and is 0.
  const openClaims = dashData?.openClaims ?? null;
  const avgCycleDays = dashData?.avgCycleDays ?? null;
  const pendingReviews = dashData?.pendingReviews ?? null;
  const complianceScore = dashData?.complianceScore ?? null;
  const totalReserve = dashData?.financials?.totalReserve ?? 0;

  // `claimsEnabled` was already resolved at the top of this hook via the
  // same tenantConfig selector; using it again below to gate rendered
  // widgets keeps the fetch skip + the render skip in lock-step.

  // Status donut from API — empty when tenant has no claims OR Claims
  // feature disabled. No mock rows.
  const statusBreakdown = dashData?.statusBreakdown || {};
  const STATUS_COLORS = {
    new: '#1d4ed8',
    reported: '#1d4ed8',
    open: '#15803d',
    review: '#d97706',
    investigation: '#4338ca',
    negotiation: '#9d174d',
    approved: '#065f46',
    denied: '#991b1b',
    closed: '#64748b',
    reopened: '#7c3aed',
  };
  const statusLabel = (k) => {
    // Convert any status code — 'review', 'under review', 'new' — into the
    // camelCase i18n key expected in en/fr/de. Never emits keys with spaces.
    const camel = String(k)
      .toLowerCase()
      .replace(/[-_\s]+(.)/g, (_, c) => c.toUpperCase())
      .replace(/^\s+/, '');
    return t(`status.${camel}`, { defaultValue: t('status.other', { defaultValue: 'Other' }) });
  };
  const liveStatusData = claimsEnabled
    ? Object.entries(statusBreakdown).map(([k, v]) => ({
        label: statusLabel(k),
        value: v,
        color: STATUS_COLORS[k] || '#94a3b8',
      }))
    : [];
  // null until the breakdown genuinely arrives, so the donut centre can say
  // '—' instead of asserting a total of 0 mid-bootstrap. A resolved tenant
  // with no cases still gets a real 0.
  const statusResolved = !claimsEnabled || dashData?.statusBreakdown != null;
  const totalClaimsLive = statusResolved
    ? liveStatusData.reduce((s, d) => s + d.value, 0)
    : null;

  // Volume data from API — no static fallback. Empty array when the tenant
  // has no claims OR the feature is off; the chart renders empty state.
  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // The aggregation groups by {year, month} and the year was being thrown
  // away here. The chart header therefore had nothing to derive a period
  // from and carried a hardcoded "Mar 2025 — Feb 2026" instead, which a
  // tenant whose only data is one month of 2026 read as a claim about
  // twelve months that do not exist. Keep the year.
  const liveVolumeData = claimsEnabled && Array.isArray(dashData?.volumeData) && dashData.volumeData.length > 0
    ? dashData.volumeData.map(d => ({
        month: MONTH_NAMES[d._id.month - 1] || d._id.month,
        year: d._id.year ?? null,
        value: d.count,
      }))
    : [];

  // The period the chart actually covers, from the data the chart actually
  // has. One month of data is a one-month period, not a twelve-month one.
  // Null when there is nothing to describe — the chart shows its own empty
  // state and a range label over no data would be another invention.
  const volumeRangeLabel = (() => {
    if (!liveVolumeData.length) return null;
    const label = d => (d.year ? `${d.month} ${d.year}` : String(d.month));
    const first = label(liveVolumeData[0]);
    const last  = label(liveVolumeData[liveVolumeData.length - 1]);
    return first === last ? first : `${first} — ${last}`;
  })();

  // Recent Activity had three sources and used only two of them: socket
  // events, and the audit-log feed. `dashData.recentActivity` — the ten most
  // recently touched claims, which the dashboard endpoint has always
  // returned — was never bound, so a tenant whose audit rows were all
  // filtered out as HTTP noise read "No recent activity" permanently.
  //
  // The API rows are claim-shaped ({claimNumber, status, assignedToName,
  // lastActivityAt}) while the card renders log-shaped items ({action, user,
  // module, createdAt}). They are normalised here rather than merged raw,
  // which would have rendered a column of blank lines reading "by -".
  //
  // `user` is either an object carrying a real name or null. It is never a
  // bare id and never a placeholder: the previous version passed
  // `r.assignedTo` straight through, which the API returned as an ObjectId,
  // so the card's `a.user?.name` was undefined on every row and the feed
  // read "… by -" all the way down. An unassigned case has no actor, and
  // the honest rendering of no actor is to say nothing at all.
  const apiActivity = useMemo(() => {
    const rows = Array.isArray(dashData?.recentActivity) ? dashData.recentActivity : [];
    return rows.map(r => {
      const name = typeof r.assignedToName === 'string' ? r.assignedToName.trim() : '';
      return {
        _id: r._id,
        type: 'claims',
        module: 'claims',
        // Tenant vocabulary, not a hardcoded "Claim".
        // `status` is interpolated into the visible sentence, so the raw slug
        // read "Sinistre CL-2026-00026 · closed" to a French user — the badge
        // beside it was translated while this was not. Same namespace as
        // StatusBadge, so a status reads identically wherever it appears.
        action: t('dashboard.activityClaimTouched', {
          number: r.claimNumber || '',
          status: recordStatusLabel(t, r.status),
        }),
        user: name ? { name } : null,
        createdAt: r.lastActivityAt,
        updatedAt: r.lastActivityAt,
      };
    });
  }, [dashData?.recentActivity, t]);

  // Live events first (they are newer by construction), then the fetched
  // rows, de-duplicated so a socket event about a claim does not appear
  // twice next to that claim's API row.
  const mergedActivity = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const item of [...liveActivity, ...apiActivity]) {
      const id = String(item?._id ?? '');
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      out.push(item);
    }
    return out;
  }, [liveActivity, apiActivity]);

  return {
    history,
    t,
    loading,
    dataError,
    lastUpdated,
    riskSummary,
    ackingId,
    logsList,
    onlineUsers,
    liveActivity: mergedActivity,
    upcomingDeadlines,
    reggieCount,
    signalFlash,
    metricFlash,
    fetchDashboard,
    handleAcknowledge,
    openClaims,
    avgCycleDays,
    pendingReviews,
    complianceScore,
    liveStatusData,
    totalClaimsLive,
    liveVolumeData,
    volumeRangeLabel,
    claimsEnabled,
  };
};

export default useDashboardFunctions;
